<!-->┌──────────────────────────────────────────────────────────────────────────────────────┐<- ->
<- ->│                                    Inject HTML                                       │<- ->
<- ->└──────────────────────────────────────────────────────────────────────────────────────┘<--->

<template>
    <div v-html="html"></div>
</template>

<script>
    export default {
        name: 'InjectHtml',
        props: {
            url: String,                    //  source URL to get html from
            query: {
                type: String,               //  DOM selector to extract a specific node's innerHTML from
                default: 'body'
            },
            clean: {
                type: [Boolean, String],    //  true: clean script and style nodes; false: clean nothing
                default: 'script'           //  [String]: comma-delimited list of node types to remove
            }
        },

        data() {
            return {
                html: null
            }
        },

        async mounted() {
            this.$emit('loading')

            const response = await fetch(this.url)
            if (!response.ok) console.log(`${this.url} error: ${response.statusText}`)

            const text = await response.text()
            this.html = await this.cleanHtml(text)

           this.$emit('loaded')
        },

        methods: {
            async cleanHtml(html) {
                //  Explicit false: return raw html
                if (this.clean === false) return html

                let types = (this.clean === true) ? 'script,style' : this.clean

                var htmlDoc = new DOMParser().parseFromString(html, "text/html")
                if (htmlDoc) {
                    let target = htmlDoc.querySelector(this.query)
                    if (target) {

                        //  Unless we're asked to remove them entirely, move all the <style> CSS to the top of the html
                        let getStyles = !types.includes('style'), styles = []
                        if (getStyles) types += ',style'

                        for (let s of [...target.querySelectorAll(types)]) {
                            if (getStyles && s.nodeName == "STYLE") styles.push(s.innerText)
                            s.parentNode.removeChild(s)
                        }

                        if (getStyles && styles.length) {
                            //  Inject the styles back onto the top of the target
                            let allStyles = htmlDoc.createElement('style')
                            allStyles.innerHTML = await this.gatherCSS(styles.join('\n'), this.url)

                            target.prepend(allStyles)
                        }

                        return target.innerHTML
                    }

                    return null
                }
            },

            getBaseUrl(url, goBack) {
                return url && url.split('/').slice(0, -(goBack || 1)).join('/')
            },

            fixUrls(css, url) {
                //  Try to replace relative url links in the CSS with absolute path based on CSS source
                let baseUrl = this.getBaseUrl(url, 1)
                let parentUrl = this.getBaseUrl(url, 2)

                return css
                    .replace(/url\((['"]?)\.\//g, `url($1${baseUrl}/`)
                    .replace(/url\((['"]?)\.\.\//g, `url($1${parentUrl}/`)
            },

            async gatherCSS(css, url) {
                if (!css) return css

                //  Fix URLs that have relative references
                css = this.fixUrls(css, url)

                let baseUrl = this.getBaseUrl(url)

                //  Join all style lines into one big array of CSS
                let output = css.split(/\r?\n/g)

                //  Look for lines that start with @import and store the output line number and the @import url
                let links = output
                    .map((text, number) => ({ number, link: text.split('@import ') }))
                    .filter(line => line.link[1] && !line.link[0])
                    .map(line => ({
                        line: line.number,
                        url: line.link[1].replace(/('|"|;)/g, "")
                    }))
                    //  Fix @import statements that don't have absolute references by prepending the base URL to them
                    .map(line => ({
                        line: line.line,
                        url: line.url.match(/^(http|\/)/) ? line.url : `${baseUrl}/${line.url}`
                    }))

                //  If there are no links, we don't need to await any fetches - we can just output the other CSS
                if (links.length) {
                    //  Fetch all the @import urls' CSS and insert each at the point it was declared
                    const updates = await Promise.all(links.map(
                        link => fetch(link.url).then(response => response.text())
                    ))

                    for (let idx in updates) {
                        let link = links[idx], line = link.line
                        output[line] = `/* ${output[line]} */\n`
                        output[line] += await this.gatherCSS(updates[idx], link.url)
                    }
                }

                return output.join('\n')
            }
        }
    }
</script>